import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../hooks/useApi';
import Icon from '../components/Icon';

// ─── Bloc catégorie de tests ───────────────────────────────────────────────────
function CategorieBloc({ cat }) {
  const allOk = cat.nb_ok === cat.nb_total;
  return (
    <div style={{ marginBottom: 'var(--space-5)' }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 'var(--space-2)',
        marginBottom: 'var(--space-2)',
      }}>
        <Icon
          name={allOk ? 'succes' : 'erreur'}
          size={16}
          color={allOk ? 'var(--success)' : 'var(--danger)'}
        />
        <span style={{
          fontFamily: 'Cinzel', fontSize: 12, fontWeight: 600,
          textTransform: 'uppercase', letterSpacing: '.08em',
          color: allOk ? 'var(--success)' : 'var(--danger)',
        }}>
          {cat.categorie}
        </span>
        <span className={`badge ${allOk ? 'badge--success' : 'badge--danger'}`} style={{ marginLeft: 'auto' }}>
          {cat.nb_ok} / {cat.nb_total}
        </span>
      </div>
      <table className="esc-table" style={{ fontSize: 13 }}>
        <tbody>
          {cat.tests.map((t, i) => (
            <tr key={i}>
              <td style={{ width: 24, verticalAlign: 'top', paddingRight: 4 }}>
                <Icon
                  name={t.ok ? 'check' : 'erreur'}
                  size={14}
                  color={t.ok ? 'var(--success)' : 'var(--danger)'}
                />
              </td>
              <td style={{ verticalAlign: 'top' }}>
                <div style={{ fontFamily: 'monospace', fontSize: 12 }}>{t.nom}</div>
                {t.explication && (
                  <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>{t.explication}</div>
                )}
                {t.sql && (
                  <details style={{ marginTop: 4 }}>
                    <summary style={{ cursor: 'pointer', color: 'var(--accent)', fontSize: 11 }}>SQL</summary>
                    <pre style={{
                      background: 'var(--bg)', padding: 6, borderRadius: 3,
                      fontSize: 10, margin: '4px 0 0', whiteSpace: 'pre-wrap',
                      border: '1px solid var(--border)',
                    }}>{t.sql}</pre>
                  </details>
                )}
              </td>
              <td className="muted" style={{ fontSize: 12, verticalAlign: 'top' }}>
                {t.erreur
                  ? <span style={{ color: 'var(--danger)' }}>{t.erreur}</span>
                  : (t.valeur || '')}
                {t.duree_ms != null && <span className="muted"> ({t.duree_ms}ms)</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Bloc bug ────────────────────────────────────────────────────────────────
function BugBloc({ bug, index }) {
  const [ouvert, setOuvert] = useState(false);
  const [copie, setCopie]   = useState(false);

  const couleurSeverite = {
    critique:  'var(--danger)',
    important: 'var(--warning)',
    mineur:    'var(--muted)',
  }[bug.severite] || 'var(--muted)';

  const badgeSeverite = {
    critique:  'badge--danger',
    important: 'badge--warning',
    mineur:    '',
  }[bug.severite] || '';

  function copierPrompt() {
    const texte = bug.prompt_correction;
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(texte).catch(() => fallback(texte));
    } else {
      fallback(texte);
    }
    setCopie(true);
    setTimeout(() => setCopie(false), 1500);
  }

  function fallback(texte) {
    const el = document.createElement('textarea');
    el.value = texte;
    el.style.position = 'fixed'; el.style.opacity = '0';
    document.body.appendChild(el); el.select();
    document.execCommand('copy'); document.body.removeChild(el);
  }

  return (
    <div style={{
      background: 'var(--surface)',
      border: '1px solid var(--border)',
      borderLeft: '3px solid var(--accent)',
      padding: 'var(--space-4) var(--space-5)',
      margin: 'var(--space-3) 0',
      borderRadius: 'var(--radius-md)',
      transition: 'border-color var(--t-fast)',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-3)', marginBottom: 'var(--space-3)' }}>
        <span className={`badge ${badgeSeverite}`}>{(bug.severite || '').toUpperCase()}</span>
        <span style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--text-soft)' }}>{bug.test_nom}</span>
      </div>
      <p style={{ margin: '0 0 var(--space-2)', fontSize: 13 }}><strong>Cause :</strong> {bug.cause_probable}</p>
      <p style={{ margin: '0 0 var(--space-3)', fontSize: 13 }}>
        <strong>Fichiers :</strong> {(bug.fichiers_concernes || []).map(f => (
          <code key={f} style={{
            background: 'var(--bg)', border: '1px solid var(--border)',
            padding: '2px 6px', borderRadius: 3, marginRight: 6, fontSize: 11,
          }}>{f}</code>
        ))}
      </p>
      <button
        className="btn btn-ghost btn--sm"
        onClick={() => setOuvert(o => !o)}
      >
        <Icon name={ouvert ? 'voir' : 'liste_puces'} size={13} />
        {ouvert ? 'Masquer' : 'Voir'} le prompt Claude Code
      </button>
      {ouvert && (
        <div style={{ marginTop: 'var(--space-3)' }}>
          <button
            className="btn btn-ghost btn--sm"
            style={{ marginBottom: 'var(--space-2)' }}
            onClick={copierPrompt}
          >
            <Icon name={copie ? 'check' : 'liste'} size={13} />
            {copie ? 'Copie' : 'Copier le prompt'}
          </button>
          <pre style={{
            background: 'var(--bg)', color: 'var(--text)',
            border: '1px solid var(--border)',
            padding: 'var(--space-3)', borderRadius: 'var(--radius-sm)',
            fontSize: 11, whiteSpace: 'pre-wrap', overflowX: 'auto', margin: 0,
          }}>{bug.prompt_correction}</pre>
        </div>
      )}
    </div>
  );
}

// ─── Composant principal ──────────────────────────────────────────────────────
export default function AgentDebug() {
  const [rapports, setRapports] = useState([]);
  const [selection, setSelection] = useState(null);
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lancement, setLancement] = useState(false);
  const [msg, setMsg] = useState('');

  const charger = useCallback(async () => {
    try {
      setLoading(true);
      const r = await api.get('/agent-debug/rapports');
      setRapports(r);
      if (r.length > 0) setSelection(s => s || r[0].nom);
    } catch (e) {
      setMsg(`Erreur : ${e.response?.data?.error || e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  useEffect(() => {
    if (!selection) { setDetails(null); return; }
    api.get(`/agent-debug/rapports/${selection}`).then(setDetails).catch(() => setDetails(null));
  }, [selection]);

  async function lancer() {
    if (lancement) return;
    if (!window.confirm("Lancer un run complet de l'agent debug ? (~30-60s)")) return;
    try {
      setLancement(true);
      setMsg('Agent lancé — le rapport apparaîtra dans 30-60 secondes');
      await api.post('/agent-debug/run', {});
      setTimeout(() => { charger(); setLancement(false); setMsg(''); }, 50000);
    } catch (e) {
      setMsg(`Erreur : ${e.response?.data?.error || e.message}`);
      setLancement(false);
    }
  }

  const rapport = details?.rapport;
  const analyse = details?.analyse;
  const nbEchecs = rapport?.nb_echecs || 0;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 'var(--space-4)', alignItems: 'start' }}>

      {/* ── Colonne gauche : liste des rapports ── */}
      <div className="card" style={{ padding: 0, maxHeight: 'calc(100vh - 100px)', overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: 'var(--space-4)', borderBottom: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-3)' }}>
            <h2 style={{ fontFamily: 'Cinzel', fontSize: 14, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--text-soft)', margin: 0 }}>
              Rapports
            </h2>
            <button className="btn btn-ghost btn--sm" onClick={charger} disabled={loading} title="Recharger">
              <Icon name="rafraichir" size={14} />
            </button>
          </div>
          <button className="btn btn-primary btn--block btn--sm" style={{ marginBottom: 'var(--space-3)' }} onClick={lancer} disabled={lancement}>
            <Icon name={lancement ? 'timer' : 'demarrer'} size={14} />
            {lancement ? 'Agent en cours…' : 'Lancer un run maintenant'}
          </button>
          {msg && (
            <div style={{ fontSize: 12, padding: '8px 10px', background: 'var(--surface)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', color: 'var(--muted)' }}>
              {msg}
            </div>
          )}
        </div>

        <div style={{ padding: 'var(--space-3)', flex: 1 }}>
          {loading && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
              {[0,1,2].map(i => (
                <div key={i} className="skeleton" style={{ height: 52, borderRadius: 'var(--radius-md)' }} />
              ))}
            </div>
          )}
          {!loading && rapports.length === 0 && (
            <div className="empty-state" style={{ padding: 'var(--space-6) var(--space-3)' }}>
              <Icon name="analytics" size={28} />
              <span className="empty-state__titre">Aucun rapport</span>
              <span>En prod, l'agent tourne a 8h, 13h et 18h.</span>
            </div>
          )}
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
            {rapports.map(r => {
              const echecs = r.nb_echecs || 0;
              const date   = r.timestamp ? new Date(r.timestamp) : null;
              const actif  = selection === r.nom;
              return (
                <li key={r.nom}>
                  <button
                    onClick={() => setSelection(r.nom)}
                    style={{
                      display: 'flex', flexDirection: 'column', width: '100%', textAlign: 'left',
                      padding: '10px var(--space-3)',
                      background: actif ? 'var(--surface)' : 'transparent',
                      border: `1px solid ${actif ? 'var(--accent)' : 'var(--border)'}`,
                      borderLeft: `3px solid ${echecs === 0 ? 'var(--success)' : 'var(--danger)'}`,
                      borderRadius: 'var(--radius-md)', cursor: 'pointer', color: 'var(--text)',
                      gap: 3, transition: 'border-color var(--t-fast), background var(--t-fast)',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                      <Icon
                        name={echecs === 0 ? 'succes' : 'erreur'}
                        size={13}
                        color={echecs === 0 ? 'var(--success)' : 'var(--danger)'}
                      />
                      <span style={{ fontWeight: 600, fontSize: 13 }}>{r.nb_ok}/{r.nb_total} tests</span>
                      {echecs > 0 && <span className="badge badge--danger" style={{ marginLeft: 'auto', fontSize: 10 }}>{echecs} echec(s)</span>}
                    </div>
                    <div className="muted" style={{ fontSize: 11 }}>
                      {date ? date.toLocaleString('fr-CA') : r.nom}
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      {/* ── Colonne droite : détails ── */}
      <div className="card rise-in">
        {!selection || !rapport ? (
          <div className="empty-state">
            <Icon name="analytics" size={36} />
            <span className="empty-state__titre">
              {rapports.length === 0 ? 'Aucun rapport disponible' : 'Sélectionne un rapport à gauche'}
            </span>
          </div>
        ) : (
          <>
            {/* En-tête rapport */}
            <div style={{ borderBottom: '1px solid var(--border)', paddingBottom: 'var(--space-4)', marginBottom: 'var(--space-5)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', marginBottom: 'var(--space-2)' }}>
                <Icon
                  name={nbEchecs === 0 ? 'succes' : 'erreur'}
                  size={22}
                  color={nbEchecs === 0 ? 'var(--success)' : 'var(--danger)'}
                />
                <h1 style={{
                  fontFamily: 'Cinzel', fontSize: 22, fontWeight: 600,
                  color: nbEchecs === 0 ? 'var(--success)' : 'var(--danger)',
                  margin: 0,
                }}>
                  {nbEchecs === 0 ? 'Tout OK' : `${nbEchecs} echec(s)`}
                </h1>
                <span className={`badge ${nbEchecs === 0 ? 'badge--success' : 'badge--danger'}`} style={{ marginLeft: 'auto' }}>
                  {rapport.nb_ok}/{rapport.nb_total} tests
                </span>
              </div>
              <div className="muted" style={{ fontSize: 12 }}>
                {rapport.duree_ms}ms · {new Date(rapport.timestamp).toLocaleString('fr-CA')} · cible : {rapport.target}
              </div>
            </div>

            {/* Résumé Claude */}
            {analyse?.resume && (
              <div style={{
                background: 'var(--surface)', border: '1px solid var(--border)',
                padding: 'var(--space-4)', borderRadius: 'var(--radius-md)',
                marginBottom: 'var(--space-5)',
              }}>
                <em style={{ color: 'var(--text-soft)', fontSize: 13 }}>{analyse.resume}</em>
                {analyse.priorite_action && (
                  <div style={{ marginTop: 'var(--space-3)', color: 'var(--accent)', fontSize: 13 }}>
                    <strong>Priorité :</strong> {analyse.priorite_action}
                  </div>
                )}
              </div>
            )}

            {/* Tests par catégorie */}
            {rapport.categories.map(cat => <CategorieBloc key={cat.categorie} cat={cat} />)}

            {/* Bugs détectés par Claude */}
            {analyse?.bugs?.length > 0 && (
              <>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 'var(--space-3)',
                  marginTop: 'var(--space-6)', marginBottom: 'var(--space-4)',
                  paddingBottom: 'var(--space-3)', borderBottom: '1px solid var(--border)',
                }}>
                  <Icon name="idee" size={18} color="var(--accent)" />
                  <h2 style={{ fontFamily: 'Cinzel', fontSize: 15, fontWeight: 600, margin: 0, color: 'var(--text-soft)', textTransform: 'uppercase', letterSpacing: '.08em' }}>
                    Analyse Claude
                  </h2>
                  <span className="badge badge--warning" style={{ marginLeft: 'auto' }}>
                    {analyse.bugs.length} bug(s)
                  </span>
                </div>
                {analyse.bugs.map((bug, i) => <BugBloc key={i} bug={bug} index={i} />)}
              </>
            )}

            {/* Réponse brute si non parsée */}
            {analyse?.raw_response && !analyse?.bugs?.length && (
              <div>
                <h3 style={{ fontFamily: 'Cinzel', fontSize: 13, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--muted)', marginBottom: 'var(--space-3)' }}>
                  Réponse Claude (non parsée)
                </h3>
                <pre style={{
                  background: 'var(--bg)', border: '1px solid var(--border)',
                  padding: 'var(--space-3)', borderRadius: 'var(--radius-sm)',
                  fontSize: 11, whiteSpace: 'pre-wrap',
                }}>
                  {analyse.raw_response}
                </pre>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
